import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Trash,
  Edit,
  Mail,
  Package,
  Settings,
  X,
  Filter,
  Eye,
  PlusCircle,
} from "lucide-react";
import { supabase } from "../../auth/SupabaseClient";

interface Order {
  id: string;
  order_number: string;
  client_id: string;
  client_name: string;
  status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
  delivery_method: string;
  payment_status: string;
  payment_method: string;
  total_amount: number;
  delivery_address: string;
  expected_delivery_date: string;
  notes: string;
  created_at: string;
  updated_at: string;
  user_id: string;
}

const Orders: React.FC = () => {
  const { darkMode } = useOutletContext<{ darkMode: boolean }>();
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof Order>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isGroupSelectionActive, setIsGroupSelectionActive] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGroupActionDropdownOpen, setIsGroupActionDropdownOpen] =
    useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "All" | "pending" | "processing" | "shipped" | "delivered" | "cancelled"
  >("All");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const statusFilterRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLDivElement>(null);
  const groupActionDropdownRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);
  const orderActionDropdownRef = useRef<HTMLDivElement>(null);

  // Custom hook to handle clicks outside of a specified element
  const useClickOutside = (
    ref: React.RefObject<HTMLElement>,
    handler: () => void
  ) => {
    useEffect(() => {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return;
        }
        handler();
      };

      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    }, [ref, handler]);
  };

  // Apply click outside handlers
  useClickOutside(statusFilterRef, () => setIsStatusFilterOpen(false));
  useClickOutside(settingsButtonRef, () => setIsSubmenuOpen(false));
  useClickOutside(groupActionDropdownRef, () =>
    setIsGroupActionDropdownOpen(false)
  );
  useClickOutside(orderActionDropdownRef, () => setOpenDropdown(null));

  // Fetch orders from Supabase
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          clients (
            name
          )
        `
        )
        .order(sortField, { ascending: sortDirection === "asc" });

      if (error) throw error;

      const ordersWithClientNames = data.map((order) => ({
        ...order,
        client_name: order.clients.name,
      }));

      setOrders(ordersWithClientNames as Order[]);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setError("An error occurred while fetching orders. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [sortField, sortDirection]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const { top } = tableHeaderRef.current.getBoundingClientRect();
        tableHeaderRef.current.style.position = top <= 0 ? "sticky" : "static";
        tableHeaderRef.current.style.top = top <= 0 ? "0" : "";
        tableHeaderRef.current.style.zIndex = top <= 0 ? "10" : "";
      }
    };

    const tableContainer = document.querySelector(".overflow-y-auto");
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter(
      (order) =>
        (String(order.order_number)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
          order.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.delivery_address
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          order.payment_method
            .toLowerCase()
            .includes(searchTerm.toLowerCase())) &&
        (statusFilter === "All" || order.status === statusFilter)
    );
  }, [orders, searchTerm, statusFilter]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredOrders, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const handleSort = (field: keyof Order) => {
    setSortDirection(
      field === sortField && sortDirection === "asc" ? "desc" : "asc"
    );
    setSortField(field);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const toggleDropdown = (orderId: string) => {
    setOpenDropdown(openDropdown === orderId ? null : orderId);
  };

  const handleDropdownAction = (action: string, orderId: string) => {
    if (action === "Voir la commande") {
      navigate(`/dashboard/orders/${orderId}`);
    } else {
      console.log(`${action} for order ${orderId}`);
    }
    setOpenDropdown(null);
  };

  const toggleGroupSelection = () => {
    if (isGroupSelectionActive) {
      setIsExiting(true);
      setTimeout(() => {
        setIsGroupSelectionActive(false);
        setSelectedOrders([]);
        setIsSubmenuOpen(false);
        setIsGroupActionDropdownOpen(false);
        setIsExiting(false);
      }, 300);
    } else {
      setIsGroupSelectionActive(true);
      setSelectedOrders([]);
      setIsSubmenuOpen(false);
    }
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleAllSelection = () => {
    setSelectedOrders(
      selectedOrders.length === paginatedOrders.length
        ? []
        : paginatedOrders.map((order) => order.id)
    );
  };

  const handleBulkAction = (action: "delete" | "edit" | "email") => {
    console.log(`Bulk ${action} for orders:`, selectedOrders);
    setIsGroupActionDropdownOpen(false);
  };

  const renderSearchBar = () => (
    <div className="relative flex-grow md:mr-4">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      <input
        type="text"
        placeholder="Rechercher..."
        className={`w-full pl-10 pr-4 py-2 rounded-lg ${
          darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-800"
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
  );

  const renderStatusFilter = () => (
    <div className="relative" ref={statusFilterRef}>
      <button
        onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          darkMode
            ? "bg-gray-700 text-white hover:bg-gray-600"
            : "bg-gray-200 text-gray-800 hover:bg-gray-300"
        }`}
      >
        <Filter className="h-4 w-4 inline-block mr-2" />
        {statusFilter === "All" ? "Tous" : statusFilter}
      </button>
      {isStatusFilterOpen && (
        <div
          className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-20 ${
            darkMode ? "bg-gray-700" : "bg-white"
          } ring-1 ring-black ring-opacity-5 z-10`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {[
              "All",
              "pending",
              "processing",
              "shipped",
              "delivered",
              "cancelled",
            ].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setStatusFilter(status as typeof statusFilter);
                  setIsStatusFilterOpen(false);
                }}
                className={`block px-4 py-2 text-sm w-full text-left ${
                  darkMode
                    ? "text-gray-300 hover:bg-gray-600"
                    : "text-gray-700 hover:bg-gray-100"
                } ${statusFilter === status ? "bg-blue-500 text-white" : ""}`}
                role="menuitem"
              >
                {status === "All" ? "Tous" : status}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSettingsButton = () => (
    <div className="relative" ref={settingsButtonRef}>
      <button
        onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
        className={`pt-1.5 pb-2 pr-3 pl-3 rounded-lg ${
          darkMode
            ? "bg-gray-700 text-white hover:bg-gray-600"
            : "bg-gray-200 text-gray-800 hover:bg-gray-300"
        }`}
      >
        <Settings className="h-4 w-4 inline-block" size={20} />
      </button>
      {isSubmenuOpen && (
        <div
          className={`z-20 absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
            darkMode ? "bg-gray-700" : "bg-white"
          } ring-1 ring-black ring-opacity-5 z-10`}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            <button
              onClick={() => {
                toggleGroupSelection();
                setIsSubmenuOpen(false);
              }}
              className={`block px-4 py-2 text-sm w-full text-left ${
                darkMode
                  ? "text-gray-300 hover:bg-gray-600"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              role="menuitem"
            >
              <Package className="h-4 w-4 inline-block mr-2" />
              Sélection groupée
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderGroupActionButtons = () => (
    <div
      className={`flex items-center space-x-2 transition-all duration-300 ease-in-out pt-5 ${
        isExiting
          ? "opacity-0 transform -translate-y-2"
          : "opacity-100 transform translate-y-0 animate-fade-in-down"
      }`}
      style={{
        animation: isExiting ? "fadeOutUp 0.3s ease-out forwards" : "",
      }}
    >
      <div className="relative" ref={groupActionDropdownRef}>
        <button
          onClick={() =>
            setIsGroupActionDropdownOpen(!isGroupActionDropdownOpen)
          }
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Actions groupées ({selectedOrders.length})
          <ChevronDown className="h-4 w-4 inline-block ml-2" />
        </button>
        {isGroupActionDropdownOpen && (
          <div
            className={`absolute left-0 mt-2 w-48 rounded-md shadow-lg ${
              darkMode ? "bg-gray-700" : "bg-white"
            } ring-1 ring-black ring-opacity-5 z-20`}
          >
            <div className="py-1" role="menu" aria-orientation="vertical">
              {["delete", "edit", "email"].map((action) => (
                <button
                  key={action}
                  onClick={() =>
                    handleBulkAction(action as "delete" | "edit" | "email")
                  }
                  className={`block px-4 py-2 text-sm w-full text-left ${
                    darkMode
                      ? "text-gray-300 hover:bg-gray-600"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  role="menuitem"
                >
                  {action === "delete" && (
                    <Trash className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action === "edit" && (
                    <Edit className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action === "email" && (
                    <Mail className="h-4 w-4 inline-block mr-2" />
                  )}
                  {action.charAt(0).toUpperCase() + action.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={toggleGroupSelection}
        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        <X className="h-4 w-4 inline-block mr-2" />
        Annuler
      </button>
    </div>
  );

  const renderOrderTable = () => (
    <div className="flex-grow px-4 md:px-6">
      <div
        className={`overflow-x-auto overflow-y-auto max-h-[calc(100vh-600px)] min-h-[calc(100vh-350px)] rounded-lg border ${
          darkMode ? "border-gray-600" : "border-gray-300"
        }`}
      >
        <div className="inline-block min-w-full align-middle">
          <table
            className={`min-w-full rounded-lg ${
              darkMode ? "bg-gray-700" : "bg-white"
            }`}
          >
            <thead
              ref={tableHeaderRef}
              className={`${
                darkMode ? "bg-gray-800" : "bg-gray-100"
              } sticky top-0 z-10`}
            >
              <tr>
                {isGroupSelectionActive && (
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrders.length === paginatedOrders.length}
                      onChange={toggleAllSelection}
                      className={`form-checkbox h-5 w-5 ${
                        darkMode
                          ? "text-blue-500 border-gray-600 bg-gray-700"
                          : "text-blue-600 border-gray-300 bg-gray-100"
                      }`}
                    />
                  </th>
                )}
                {[
                  "order_number",
                  "client_name",
                  "created_at",
                  "status",
                  "total_amount",
                  "payment_status",
                ].map((field) => (
                  <th
                    key={field}
                    scope="col"
                    className={`px-6 py-3 text-left text-xs font-medium ${
                      darkMode ? "text-gray-300" : "text-gray-500"
                    } uppercase tracking-wider cursor-pointer`}
                    onClick={() => handleSort(field as keyof Order)}
                  >
                    <div className="flex items-center">
                      {field === "order_number"
                        ? "Numéro"
                        : field === "client_name"
                        ? "Client"
                        : field === "created_at"
                        ? "Date"
                        : field === "total_amount"
                        ? "Total"
                        : field === "payment_status"
                        ? "Paiement"
                        : field.charAt(0).toUpperCase() + field.slice(1)}
                      {sortField === field &&
                        (sortDirection === "asc" ? (
                          <ChevronUp className="ml-1 h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-1 h-4 w-4" />
                        ))}
                    </div>
                  </th>
                ))}
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody
              className={`${darkMode ? "bg-gray-900" : "bg-white"} divide-y ${
                darkMode ? "divide-gray-800" : "divide-gray-200"
              }`}
            >
              {paginatedOrders.map((order) => (
                <tr
                  key={order.id}
                  onClick={() => handleRowClick(order.id)}
                  className={`cursor-pointer transition-colors duration-150 ease-in-out ${
                    darkMode ? "hover:bg-gray-800" : "hover:bg-gray-50"
                  }`}
                >
                  {isGroupSelectionActive && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleOrderSelection(order.id);
                        }}
                        className={`form-checkbox h-5 w-5 ${
                          darkMode
                            ? "text-blue-500 border-gray-600 bg-gray-700"
                            : "text-blue-600 border-gray-300 bg-gray-100"
                        }`}
                      />
                    </td>
                  )}
                  <td
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    #{order.order_number}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {order.client_name}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {new Date(order.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.status === "shipped"
                          ? "bg-blue-100 text-blue-800"
                          : order.status === "delivered"
                          ? "bg-green-100 text-green-800"
                          : order.status === "cancelled"
                          ? "bg-red-100 text-red-800"
                          : order.status === "processing"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {order.status.charAt(0).toUpperCase() +
                        order.status.slice(1)}
                    </span>
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    ${order.total_amount.toFixed(2)}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap ${
                      darkMode ? "text-gray-300" : "text-gray-900"
                    }`}
                  >
                    {order.payment_status}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDropdown(order.id);
                      }}
                      className={`text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                        darkMode ? "hover:text-gray-300" : "hover:text-gray-700"
                      }`}
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>
                    {openDropdown === order.id && (
                      <div
                        className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${
                          darkMode ? "bg-gray-700" : "bg-white"
                        } ring-1 ring-black ring-opacity-5 z-10`}
                      >
                        <div
                          className="py-1"
                          role="menu"
                          aria-orientation="vertical"
                        >
                          {[
                            "Voir la commande",
                            "Modifier le statut",
                            "Envoyer un email",
                          ].map((action) => (
                            <button
                              key={action}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDropdownAction(action, order.id);
                              }}
                              className={`block px-4 py-2 text-sm ${
                                darkMode
                                  ? "text-gray-300 hover:bg-gray-600"
                                  : "text-gray-700 hover:bg-gray-100"
                              } w-full text-left`}
                              role="menuitem"
                            >
                              {action === "Voir la commande" && (
                                <Eye className="inline-block mr-2 h-4 w-4" />
                              )}
                              {action === "Modifier le statut" && (
                                <Edit className="inline-block mr-2 h-4 w-4" />
                              )}
                              {action === "Envoyer un email" && (
                                <Mail className="inline-block mr-2 h-4 w-4" />
                              )}
                              {action}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paginatedOrders.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64">
            <p
              className={`text-xl font-semibold ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Aucune commande trouvée
            </p>
            <p
              className={`mt-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Essayez de modifier vos filtres ou votre recherche
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderPagination = () => (
    <div className="p-4 md:p-6">
      {filteredOrders.length > 0 ? (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <span
              className={`mr-2 text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Résultats par page:
            </span>
            <select
              className={`px-2 py-1 rounded-lg ${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-gray-100 text-gray-800"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={itemsPerPage.toString()}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>
          <div className="flex items-center justify-between w-full md:w-auto">
            <p
              className={`text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } mr-4`}
            >
              Affichage de {(currentPage - 1) * itemsPerPage + 1} à{" "}
              {Math.min(currentPage * itemsPerPage, filteredOrders.length)} sur{" "}
              {filteredOrders.length} résultats
            </p>
            <div className="flex space-x-2">
              <button
                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`px-3 py-1 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      page === currentPage
                        ? "bg-blue-500 text-white border-blue-500"
                        : darkMode
                        ? "text-gray-300 border-gray-600 hover:bg-gray-700"
                        : "text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                className={`px-3 py-1 border rounded-md text-sm font-medium ${
                  darkMode
                    ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between">
          <div className="flex items-center mb-4 md:mb-0">
            <span
              className={`mr-2 text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Résultats par page:
            </span>
            <select
              className={`px-2 py-1 rounded-lg ${
                darkMode
                  ? "bg-gray-700 text-white"
                  : "bg-gray-100 text-gray-800"
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={itemsPerPage.toString()}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="20">20</option>
            </select>
          </div>
          <div className="flex items-center justify-between w-full md:w-auto">
            <p
              className={`text-sm ${
                darkMode ? "text-gray-300" : "text-gray-700"
              } mr-4`}
            >
              Aucun résultat trouvé
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const handleAddOrder = () => {
    // Navigate to the add order page or open a modal
    console.log("Navigating to add order page");
    navigate("/dashboard/orders/add");
    // Uncomment the following line when you have the route set up
    // navigate('/dashboard/orders/add');
  };

  const handleRowClick = (orderId: string) => {
    navigate(`/dashboard/orders/${orderId}`);
  };

  const renderAddOrderButton = () => (
    <button
      onClick={handleAddOrder}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
        darkMode
          ? "bg-blue-600 text-white hover:bg-blue-700"
          : "bg-blue-500 text-white hover:bg-blue-600"
      }`}
    >
      <PlusCircle className="h-4 w-4 inline-block mr-2" />
      Nouvelle Commande
    </button>
  );

  return (
    <div
      className={`rounded-lg flex flex-col min-h-11 ${
        darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-800"
      }`}
    >
      <div className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {renderSearchBar()}
          <div className="flex items-center space-x-4">
            {renderStatusFilter()}
            {renderSettingsButton()}
            {renderAddOrderButton()} {/* New Add Order button */}
          </div>
        </div>

        {(isGroupSelectionActive || isExiting) && renderGroupActionButtons()}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-500 mt-4">{error}</div>
      ) : (
        renderOrderTable()
      )}

      {renderPagination()}
    </div>
  );
};

export default Orders;
